import { Form } from "@/components/form/form";
import { FormField } from "@/components/form/form-field/form-field";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { IconButton } from "@/components/ui/icon-button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/hooks/auth/auth";
import { normalizeAuthRedirect } from "@/lib/auth-redirect";
import { toast } from "sonner";
import { useAuthLogin, useAuthRequestMagicLink } from "@/orval/auth";
import type { LoginDTO } from "@/orval/model";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { LogIn, Sparkles, UserPlus2, Lock, Mail } from "lucide-react";
import { useEffect, useLayoutEffect, useState, type Dispatch, type SetStateAction } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { z } from "zod";

const loginSearchSchema = z.object({
  redirect: z.string().optional(),
  message: z.string().optional(),
  messageType: z.enum(["error", "info"]).optional(),
  tab: z.string().optional(),
});

const defaultValues: LoginDTO = {
  userNameOrEmail: "",
  password: "",
};

const MagicLoginButton = ({
  cooldown,
  setCooldown,
  setTimer,
}: {
  cooldown: number;
  setCooldown: Dispatch<SetStateAction<number>>;
  setTimer: Dispatch<SetStateAction<ReturnType<typeof setInterval> | null>>;
}) => {
  const userNameOrEmail = useWatch<LoginDTO>({ name: "userNameOrEmail" });
  const { formState } = useFormContext<LoginDTO>();

  const { mutateAsync: sendMagicLinkAsync, isPending } = useAuthRequestMagicLink();

  const handleSendMagicLink = async () => {
    if (!userNameOrEmail || cooldown > 0) {
      return;
    }

    try {
      const result = await sendMagicLinkAsync({ data: { email: userNameOrEmail } });

      if (result.message) {
        toast.success(result.message);

        // Start the 30 second cooldown
        setCooldown(30);

        const interval = setInterval(() => {
          setCooldown((prevCooldown) => {
            if (prevCooldown <= 1) {
              clearInterval(interval);
              setTimer(null);
              return 0;
            }
            return prevCooldown - 1;
          });
        }, 1000);

        setTimer(interval);
      }
    } catch (error) {
      console.error("Failed to send magic link:", error);
    }
  };

  let buttonLabel = "Send Magic Link";
  if (cooldown > 0) {
    buttonLabel = `Magic link sent! Wait ${cooldown}s to try again`;
  } else if (isPending) {
    buttonLabel = "Sending magic link...";
  }

  return (
    <IconButton
      className="w-full"
      type="submit"
      icon={<Sparkles />}
      onClick={handleSendMagicLink}
      disabled={!!formState.errors["userNameOrEmail"] || cooldown > 0 || isPending}
    >
      {buttonLabel}
    </IconButton>
  );
};

const Login = () => {
  const { mutateAsync: loginAsync } = useAuthLogin();
  const { setToken, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { redirect, message, messageType, tab } = Route.useSearch();

  const [cooldown, setCooldown] = useState(0);
  const [timer, setTimer] = useState<ReturnType<typeof setInterval> | null>(null);

  const selectedTab = tab ?? "password"; // Default to password tab if not specified

  useEffect(() => {
    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [timer]);

  const onSubmit = async (data: LoginDTO) => {
    const normalizedRedirect = normalizeAuthRedirect(redirect, window.location.origin);
    // Password login
    const { token, twoFactorEnabled, hasTwoFactorProviderOptions } = await loginAsync({ data });

    if (token) {
      setToken(token);
    } else if (twoFactorEnabled && !hasTwoFactorProviderOptions) {
      toast.success("A two factor code has been sent to your email address.");

      // Navigate to the two factor code page
      navigate({
        to: "/account/two-factor-code",
        search: { provider: "Email", email: data.userNameOrEmail, redirect: normalizedRedirect },
      });
    } else if (twoFactorEnabled && hasTwoFactorProviderOptions) {
      // Navigate to the two factor provider options page
      navigate({
        to: "/account/two-factor-options",
        search: { email: data.userNameOrEmail, redirect: normalizedRedirect },
      });
    } else {
      toast.error("Login failed, no token returned and two factor authentication is not enabled.");
    }
  };

  // Ah, the subtle nuances of client side auth. 🙄
  useLayoutEffect(() => {
    if (isAuthenticated) {
      window.location.href = normalizeAuthRedirect(redirect, window.location.origin);
    }
  }, [isAuthenticated, redirect]);

  useEffect(() => {
    if (message && message !== "undefined") {
      const showToast = messageType === "info" ? toast.info : toast.error;
      showToast(message, { id: "login-redirect-message" });
    }
  }, [message, messageType]);

  return (
    <div className="flex items-center justify-center h-full flex-1">
      <Card className="max-w-full w-96">
        <CardHeader>
          <CardTitle>Login</CardTitle>
          <CardDescription>
            {selectedTab === "magic"
              ? "We'll send you a magic link to sign in instantly"
              : "Sign in instantly with your email and password"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs
            defaultValue="magic"
            value={selectedTab}
            onValueChange={(value) => navigate({ to: ".", search: { tab: value } })}
            className="gap-y-4"
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="password" className="flex items-center gap-2">
                <Lock className="size-4" />
                Password
              </TabsTrigger>
              <TabsTrigger value="magic" className="flex items-center gap-2">
                <Mail className="size-4" />
                Magic Link
              </TabsTrigger>
            </TabsList>

            <TabsContent value="magic" className="gap-y-4">
              <Form<LoginDTO> onSubmit={() => {}} useFormProps={{ defaultValues }}>
                <FormField
                  label="Email"
                  name="userNameOrEmail"
                  inputProps={{
                    placeholder: "Your email address",
                    autoFocus: true,
                    autoComplete: "email",
                    type: "text",
                  }}
                  rules={{ required: true }}
                  ignore1Password={false}
                />
                <MagicLoginButton cooldown={cooldown} setCooldown={setCooldown} setTimer={setTimer} />
              </Form>
            </TabsContent>

            <TabsContent value="password" className="gap-y-4">
              <Form<LoginDTO> onSubmit={onSubmit} useFormProps={{ defaultValues }}>
                <FormField
                  label="Email"
                  name="userNameOrEmail"
                  inputProps={{
                    placeholder: "Your email address",
                    autoFocus: true,
                    autoComplete: "email",
                    type: "text",
                  }}
                  rules={{ required: true }}
                  ignore1Password={false}
                />

                <FormField
                  label="Password"
                  name="password"
                  inputProps={{ placeholder: "Your password", type: "password", autoComplete: "current-password" }}
                  rules={{ required: true }}
                  ignore1Password={false}
                />

                <div className="flex justify-end py-1">
                  <Link
                    to="/forgot-password"
                    className="text-xs text-primary hover:underline font-semibold"
                    tabIndex={-1}
                  >
                    Forgot your password or need to set one up?
                  </Link>
                </div>

                <IconButton className="w-full" type="submit" icon={<LogIn />}>
                  Login
                </IconButton>
              </Form>
            </TabsContent>
          </Tabs>
        </CardContent>
        <CardFooter className="flex flex-col gap-4 justify-between mt-4">
          <small className="text-xs text-zinc-600 font-medium leading-none">Don&apos;t have an account?</small>

          <IconButton
            className="w-full"
            variant="outline"
            icon={<UserPlus2 />}
            onClick={() =>
              navigate({
                to: "/sign-up",
                search: { redirect: normalizeAuthRedirect(redirect, window.location.origin) },
              })
            }
          >
            Sign Up
          </IconButton>
        </CardFooter>
      </Card>
    </div>
  );
};

export const Route = createFileRoute("/_marketing/login")({
  preload: false,
  validateSearch: loginSearchSchema,
  ssr: false,
  component: Login,
});
